<template>
  <BaseDashboard
    :class="{ 'loader-cover': userFetchInProgress }"
    :show-new-sidebar="user.isStartup || user.isOrganizer"
  >
    <ul slot="sidenav-content" class="nav">
      <MainMenuItem
        v-show="!user.isInvestor"
        :to="userDashboardRoute"
        :class="{ active: isAtMainDashboardPage }"
        :exact="true"
        refresh-on-navigate
        :item="{
          name: user.isStartup ? 'Profile' : 'Dashboard',
          href: '/',
        }"
      >
        <span v-show="!user.isStartup" class="title">Dashboard</span>
        <span v-show="user.isStartup" class="title">Profile</span>
        <span class="ion ion-ios-home pull-right"></span>
      </MainMenuItem>

      <nav-link
        v-show="user.isOrganizer"
        :to="{ name: 'dashboardMyOrganization' }"
      >
        <span class="title">My organization</span>
        <ion-icon class="pull-right" name="briefcase" />
      </nav-link>

      <MainMenuItem
        v-show="user.canSeeStartupList"
        :to="{ name: 'dashboardStartupList' }"
        :exact="true"
        refresh-on-navigate
        :item="{
          name: 'Companies',
          href: '/dashboard/startups',
        }"
      >
        <span class="title">Companies</span>
        <span class="ion ion-grid pull-right"></span>
      </MainMenuItem>
      <SidenavLink
        v-if="$hasFeature('ORG_NEW_STARTUPS')"
        v-show="user.canSeeStartupList"
        :to="{ name: 'dashboardNewStartupList' }"
        class="subpage"
      >
        <span class="title">New</span>
        <span class="ion ion-ios-lightbulb pull-right"></span>
      </SidenavLink>
      <SidenavLink
        v-show="user.canSeeStartupList && user.isInvestor"
        :to="{ name: 'dashboardFeaturedStartups' }"
        class="subpage"
      >
        <span class="title">Featured</span>
        <span class="ion ion-ios-star pull-right"></span>
      </SidenavLink>
      <SidenavLink
        v-if="features.STARTUP_FOLLOW"
        v-show="user.canSeeStartupList"
        class="subpage"
        :to="{ name: 'shortlistListPage' }"
      >
        <span class="title">Shortlists</span>
        <span class="ion ion-thumbsup pull-right"></span>
      </SidenavLink>

      <NavLink
        v-if="user.isOrganizer && $hasFeature('ORG_FUNNEL')"
        :to="{ name: 'organizationFunnel' }"
        :class="{ 'animated pulse': followedCountChanged }"
      >
        <span class="title">Funnel</span>
        <span class="badge">{{ followedCount }}</span>
        <span class="ion ion-funnel pull-right"></span>
      </NavLink>

      <NavLink
        v-show="user.isStartup && (startup.features || {}).links"
        :to="{ name: 'dashboardStartupLinks' }"
      >
        <span class="title">Links</span>
        <span class="ion ion-shuffle pull-right"></span>
      </NavLink>

      <NavLink
        v-show="user.isStartup"
        :to="{ name: 'dashboardApplicationList' }"
      >
        <span class="title">My applications</span>
        <el-tooltip
          v-if="hasPendingApplications"
          content="At least one of your applications is incomplete. Please check and click Submit where needed"
          placement="right"
        >
          <span class="badge">!</span>
        </el-tooltip>
        <span class="ion ion-android-list pull-right"></span>
      </NavLink>

      <NavLink
        v-show="user.isStartup"
        :to="{ name: 'dashboardMarketplace' }"
        badge="new"
      >
        <span class="title">Discounts</span>
        <ion-icon class="pull-right" name="pricetag" />
      </NavLink>

      <NavLink
        v-show="user.isStartup && !startup.profile_locked"
        :to="{ name: 'dashboardStartupSettings' }"
      >
        <span class="title">Settings</span>
        <span class="ion ion-gear-a pull-right"></span>
      </NavLink>

      <NavLink
        v-show="user.isOrganizer"
        :to="{ name: 'dashboardOrganizationProgramList' }"
      >
        <span class="title">Programs</span>
        <span class="ion ion-android-list pull-right"></span>
      </NavLink>

      <nav-link
        v-if="user.isOrganizer"
        :to="{ name: 'dashboardOrganizationReports' }"
      >
        <span class="title">Reports</span>
        <ion-icon class="pull-right" name="stats-chart" />
      </nav-link>

      <NavLink
        v-show="user.isOrganizer || (user.isInvestor && user.events.length)"
        :to="{ name: 'eventList' }"
      >
        <span class="title">Showroom</span>
        <span class="ion ion-compass pull-right"></span>
      </NavLink>
      <NavLink :to="{ name: 'chatThreadList' }">
        <span class="title">Messages</span>
        <span v-if="unreadMessagesCount" class="badge">{{
          unreadMessagesCount
        }}</span>
        <ion-icon class="pull-right" name="chatbubbles" />
      </NavLink>
    </ul>
    <div slot="content">
      <MainDashboardPage v-if="isAtMainDashboardPage" />
      <router-view class="main-dashboard-panel" />
    </div>
  </BaseDashboard>
</template>

<script>
import BaseDashboard from 'modules/dashboard/BaseDashboard.vue'
import MainDashboardPage from './MainDashboardPage'
import NavLink from 'modules/dashboard/SidenavLink.vue'
import SidenavLink from 'modules/dashboard/SidenavLink.vue'
import { mapGetters, mapState } from 'vuex'
import { features } from 'utils/features'
import { getNewAppUrl } from 'utils/urls'
import MainMenuItem from '../dashboard/MainMenuItem.vue'

export default {
  components: {
    BaseDashboard,
    MainDashboardPage,
    NavLink,
    SidenavLink,
    MainMenuItem,
  },
  data: () => {
    return {
      followedCountChanged: false,
    }
  },
  watch: {
    user() {
      if (this.user.isStartup) {
        if (this.isAtMainDashboardPage) {
          console.log(
            ' => isStartup and isAtMainDashboardPage. Reloading to:',
            this.newEditorUrl,
          )
          window.location.href = this.newEditorUrl
          return
        }
        const id = this.user.startupId
        this.$store.dispatch('startupProfile/fetch', { id })
        this.$store.dispatch('startupProfile/fetchLinks', { id })
      }
    },
  },
  created() {
    this.$store.dispatch('user/fetch')
    this.$store.dispatch('startupList/fetchfollowedCount')
    this.$store.dispatch('chat/fetchThreads')
  },
  computed: {
    isAtMainDashboardPage() {
      return this.$route.name === 'dashboard'
    },
    features: () => features,
    ...mapState({
      user: (state) => state.user.data || {},
      userFetchInProgress: (state) => state.user.fetchInProgress,
      followedCount: (state) => state.startupList.followedCount,
      startup: (state) => state.startupProfile.startup,
    }),
    ...mapGetters({
      hasPendingApplications: 'startupApplications/hasPendingApplications',
      unreadMessagesCount: 'chat/unreadMessagesCount',
    }),
    userOrganization() {
      if (
        this.user &&
        this.user.organizations &&
        this.user.organizations.length
      ) {
        return this.user.organizations[0]
      } else {
        return {}
      }
    },
    newEditorUrl() {
      const url = '/dashboard/new'
      const query = this.$route.query
      return getNewAppUrl(url, query)
    },
    userDashboardRoute() {
      if (this.user.isStartup) {
        return this.newEditorUrl
      } else {
        return '/dashboard'
      }
    },
  },
  methods: {
    toggleSidebar() {
      console.log(' => toggleSidebar')
      this.sidebarVisible = !this.sidebarVisible
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.nav {
  .ion {
    font-size: 18px;
    margin-top: -1px;
  }
  .badge {
    font-size: 10px;
  }
}
</style>
